import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ChevronDown, ChevronRight, ShieldAlert, Mountain, Flag } from "lucide-react";
import { usePlanState } from "@/hooks/usePlanState";
import { UpgradePrompt } from "@/components/LockedOverlay";

const STAGE_LABELS = {
  base_camp: "Base Camp",
  trail_1: "Trail 1",
  trail_2: "Trail 2",
  trail_3: "Trail 3",
  summit: "Summit",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const QuizHistory = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [rescueBusy, setRescueBusy] = useState(false);
  const { state: planState } = usePlanState();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/students/${id}/quiz-history`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load quiz history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleRescue = async () => {
    if (!data?.climber) return;
    setRescueBusy(true);
    try {
      const next = !data.climber.rescue_mode;
      await api.post(`/students/${id}/set-rescue-mode`, { mode: next });
      toast.success(next ? "Rescue Route is ON" : "Standard trail restored");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    } finally {
      setRescueBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-mm-parchment bg-topo">
        <Navbar />
        <div className="container mx-auto py-20 text-center text-mm-navy/60">Loading quiz history…</div>
      </div>
    );
  }

  const quizzes = data.quizzes || [];
  const avgScore = quizzes.length
    ? Math.round(quizzes.reduce((a, q) => a + (q.score_percent || 0), 0) / quizzes.length)
    : 0;
  const totalAp = quizzes.reduce((a, q) => a + (q.ap_earned || 0), 0);

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10">
        <Link to="/dashboard" className="text-sm text-mm-steel font-bold hover:text-mm-navy" data-testid="quiz-history-back">
          ← Back to HQ
        </Link>

        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Climber report</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="quiz-history-title">
              {data.climber.name}'s quiz history
            </h1>
            <p className="text-mm-navy/70 mt-2 max-w-2xl text-sm">
              Every trail check and Summit quiz, broken down question-by-question — see what they aced and where they need support.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 min-w-[300px]">
            <Stat icon={<Flag size={14} />} label="Quizzes" value={quizzes.length} />
            <Stat icon={<Mountain size={14} />} label="Avg score" value={`${avgScore}%`} />
            <Stat icon={<Check size={14} />} label="Total AP" value={`+${totalAp}`} accent />
          </div>
        </div>

        {planState && !planState.is_premium && (
          <div className="mt-6" data-testid="reports-upgrade-prompt-wrap">
            <UpgradePrompt
              testid="reports-upgrade-prompt"
              icon={Flag}
              message="Detailed reports and weekly progress emails are included with Summit Pass — never miss a beat in your climber's journey."
            />
          </div>
        )}

        <div className={`mt-6 rounded-xl border-2 p-4 flex items-center justify-between gap-3 ${data.climber.rescue_mode ? "bg-mm-gold/15 border-mm-gold/40" : "bg-white border-mm-navy/10"}`} data-testid="rescue-mode-banner">          <div className="flex items-start gap-3">
            <ShieldAlert size={18} strokeWidth={2.5} className="text-mm-steel mt-0.5" />
            <div>
              <div className="font-heading font-bold text-mm-navy">
                {data.climber.rescue_mode ? "Rescue Route is ON" : "Rescue Route is off"}
              </div>
              <div className="text-xs text-mm-navy/70 mt-0.5">
                {data.climber.rescue_mode
                  ? "Future expeditions auto-generate with extra scaffolding and friendlier language."
                  : "Future expeditions use the standard trail difficulty."}
              </div>
            </div>
          </div>
          <button
            onClick={toggleRescue}
            disabled={rescueBusy}
            className={`btn-trail text-sm disabled:opacity-50 ${data.climber.rescue_mode ? "btn-outline-navy" : "btn-gold"}`}
            data-testid="toggle-rescue-mode-btn"
          >
            {data.climber.rescue_mode ? "Turn off" : "Turn on"}
          </button>
        </div>

        <div className="mt-8 space-y-3" data-testid="quiz-history-list">
          {quizzes.length === 0 && (
            <div className="text-mm-navy/60 text-sm py-10 text-center border-2 border-dashed border-mm-navy/15 rounded-xl">
              No quizzes completed yet. As your climber finishes Trail checks and Summit quizzes, their results will appear here.
            </div>
          )}
          {quizzes.map((q, idx) => {
            const key = `${q.expedition_id}-${q.stage_name}-${idx}`;
            const isOpen = !!expanded[key];
            const percent = Math.round(q.score_percent || 0);
            const scoreColor = percent >= 70 ? "text-emerald-700" : percent >= 50 ? "text-amber-700" : "text-red-700";
            const correctCount = (q.results || []).filter((r) => r.is_correct).length;
            const total = (q.results || []).length;
            return (
              <div key={key} className="rounded-xl border border-mm-navy/10 bg-white overflow-hidden" data-testid={`quiz-history-row-${idx}`}>
                <button
                  onClick={() => setExpanded((p) => ({ ...p, [key]: !isOpen }))}
                  className="w-full p-4 flex items-center justify-between gap-3 hover:bg-mm-parchment/50 text-left"
                  data-testid={`quiz-history-toggle-${idx}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? <ChevronDown size={18} className="text-mm-steel shrink-0" /> : <ChevronRight size={18} className="text-mm-steel shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-mm-navy truncate">
                        {STAGE_LABELS[q.stage_name] || q.stage_name} · {q.expedition_topic}
                      </div>
                      <div className="text-[11px] text-mm-navy/60 mt-0.5">{fmtDate(q.completed_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className={`font-heading text-xl font-black ${scoreColor}`}>{percent}%</div>
                      <div className="text-[10px] text-mm-navy/60 font-bold">{correctCount}/{total} correct</div>
                    </div>
                    <div className="hidden sm:block px-3 py-1 rounded-full bg-mm-gold/20 text-mm-navy text-xs font-bold">
                      +{q.ap_earned || 0} AP
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-mm-navy/10" data-testid={`quiz-history-detail-${idx}`}>
                    <ul className="space-y-2 mt-3">
                      {(q.results || []).map((r, ri) => (
                        <li
                          key={ri}
                          className={`rounded-lg border p-3 ${r.is_correct ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
                        >
                          <div className="flex items-start gap-2">
                            {r.is_correct
                              ? <Check size={16} strokeWidth={3} className="text-emerald-700 mt-0.5 shrink-0" />
                              : <X size={16} strokeWidth={3} className="text-red-700 mt-0.5 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-mm-navy">{r.question}</div>
                              <div className="text-xs text-mm-navy/75 mt-1 space-y-0.5">
                                {r.first_attempt_value && (
                                  <div>1st attempt: <span className={`font-bold ${r.first_correct ? "text-emerald-800" : "text-red-800"}`}>{r.first_attempt_value}</span></div>
                                )}
                                {r.second_attempt_value && (
                                  <div>2nd attempt: <span className={`font-bold ${r.is_correct && !r.first_correct ? "text-emerald-800" : "text-red-800"}`}>{r.second_attempt_value}</span></div>
                                )}
                                {!r.is_correct && (
                                  <div>Correct answer: <span className="font-bold text-emerald-800">{r.options?.[r.correct_answer_index]}</span></div>
                                )}
                              </div>
                              <div className="text-[11px] text-mm-navy/55 mt-1">
                                {r.is_correct ? (r.attempt_used === 1 ? "Got it first try" : "Got it second try") : "Missed both attempts"}
                                {` · +${r.ap_earned || 0} AP`}
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value, accent }) => (
  <div className={`p-3 rounded-lg ${accent ? "bg-mm-navy text-white" : "bg-white border border-mm-navy/10"}`}>
    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${accent ? "text-mm-gold" : "text-mm-steel"}`}>
      {icon}{label}
    </div>
    <div className={`font-heading text-xl font-black ${accent ? "text-white" : "text-mm-navy"}`}>{value}</div>
  </div>
);

export default QuizHistory;
